import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AppBar,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Theme,
  Toolbar,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { CoverageCatalog } from 'data-layer-cr/dist/models';
import { colors, moneyFormat } from 'design-system';
import { uniqueId } from 'lodash';
import React, { useMemo } from 'react';
import formatTitleCase from 'src/functions/formatTitleCase';
import CatalogItem from 'src/models/helperTypes/CatalogItem';

const { formatMoney } = moneyFormat;
const COLON_MONEY = '₡';
const ENDORSO_DORADO_CODE = '0040';

type Props = {
  endorsements: CatalogItem<CoverageCatalog>[];
  selectedEndorsement: string;
  typeOfMoney: string;
};

export function formatCase(coverage: string | undefined): string {
  if (coverage) {
    return formatTitleCase(coverage.replace('Endoso ', ''));
  }
  return '';
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleText: {
      color: colors.white,
      [theme.breakpoints.down('sm')]: {
        color: colors.madison,
      },
    },
    appBar: {
      backgroundColor: colors.madison,
      [theme.breakpoints.down('sm')]: {
        position: 'fixed',
        backgroundColor: colors.white,
      },
    },
    toolbar: { height: 50, minHeight: 50, width: '100%' },
    container: {
      width: '100%',
      margin: 0,
      backgroundColor: colors.white,
      paddingInline: '1rem',
      paddingBottom: '1rem',
    },
    styledRow: {
      '&:nth-of-type(even)': {
        backgroundColor: 'white',
      },
      '&:nth-of-type(odd)': {
        backgroundColor: '#F4F4F4',
      },
      height: 70,
    },
    topAlignCell: {
      verticalAlign: 'top',
    },
    noPaddingGrid: {
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
    },
    check: { padding: 4, paddingBottom: 6 },
    visibleCheck: { visibility: 'visible' },
    hiddenCheck: { visibility: 'hidden' },
    link: { color: colors.bright_turquoise },
    table: {
      tableLayout: 'fixed',
      paddingLeft: '10px',
    },
    dynamicCell: {
      width: '100%',
      border: 'none',
    },
    fixedCell: {
      width: 100,
      border: 'none',
      [theme.breakpoints.up('sm')]: {
        width: 130,
      },
      textAlign: 'center',
    },
    descriptionCell: {
      fontSize: '13px',
      fontWeight: 500,
    },
    informationCell: {
      fontSize: '15px',
      fontWeight: 400,
      color: '#4A4B4E',
    },
    priceCell: {
      fontSize: '17px',
      fontWeight: 900,
    },
  }),
);

const isColon = (currency: string): boolean => currency === COLON_MONEY;

const iconXMark = (
  <FontAwesomeIcon
    key={uniqueId()}
    icon={faCircleXmark}
    color="#FF7182"
    style={{ fontSize: '24px' }}
  />
);

const generateDoradoColumn = (currency: string): Array<React.ReactNode> => [
  '100%',
  'Incluido',
  '50%',
  iconXMark,
  `máx. 20 días o ${formatMoney(
    isColon(currency) ? 330_000 : 600,
    false,
    currency,
  )}`,
  iconXMark,
  iconXMark,
  `${formatMoney(isColon(currency) ? 55_550 : 100, false, currency)}`,
  `${formatMoney(isColon(currency) ? 835_000 : 1_500, false, currency)}`,
  `${formatMoney(isColon(currency) ? 5_550_000 : 10_000, false, currency)}`,
];

const generatePlatinoColumn = (currency: string): Array<React.ReactNode> => [
  '100%',
  'Incluido',
  '50%',
  `${formatMoney(isColon(currency) ? 55_000 : 100, false, currency)} x año`,
  `máx. 30 días o ${formatMoney(
    isColon(currency) ? 400_000 : 800,
    false,
    currency,
  )}`,
  `máx. 10 días o ${formatMoney(
    isColon(currency) ? 140_000 : 250,
    false,
    currency,
  )}`,
  `${formatMoney(isColon(currency) ? 5_555_000 : 10_000, false, currency)}`,
  `${formatMoney(isColon(currency) ? 275_000 : 500, false, currency)}`,
  `${formatMoney(isColon(currency) ? 1_665_000 : 3_000, false, currency)}`,
  `${formatMoney(isColon(currency) ? 8_300_000 : 15_000, false, currency)}`,
];

const generateEndorsementColumn = (
  code: string,
  currency: string,
): Array<React.ReactNode> => {
  if (code === ENDORSO_DORADO_CODE) {
    return generateDoradoColumn(currency);
  }
  return generatePlatinoColumn(currency);
};

const descriptionColumn: Array<React.ReactNode> = [
  'Reembolso de deducible por inocente',
  'No depreciación por pérdida total en autos nuevos',
  'Descuento en deducible para mujeres',
  'Descuento en deducible por buen historial (hombres)',
  <>
    Alquiler de auto <br />
    (por colisión o vuelco y robo)
  </>,
  <>
    Alquiler de Auto <br /> (por comprensivo)
  </>,
  'Responsabilidad civil por uso de remolque o trailer',
  'Robo de artículos personales',
  'Gastos funerarios',
  'Muerte accidental de conductor',
];

const generateTableSchema = (
  endorsements: CatalogItem<CoverageCatalog>[],
  currency: string,
): Array<Array<React.ReactNode>> => {
  const auxiliarArray = [descriptionColumn];
  endorsements.forEach((endorsement) => {
    auxiliarArray.push(generateEndorsementColumn(endorsement.Value, currency));
  });
  const tableSchema: Array<Array<React.ReactNode>> = [];
  const defaultLength = descriptionColumn.length;
  for (let index = 0; index < defaultLength; index += 1) {
    tableSchema.push(auxiliarArray.map((dataColumn) => dataColumn[index]));
  }
  return tableSchema;
};

const EndorsementComparison = ({
  endorsements,
  selectedEndorsement,
  typeOfMoney,
}: Props): JSX.Element => {
  const classes = useStyles();
  const tableSchema = useMemo(
    () => generateTableSchema(endorsements, typeOfMoney),
    [endorsements, typeOfMoney],
  );

  function generatePriceSubtitle(position: number): React.ReactNode {
    const coverage = endorsements[position];
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingBottom="1rem"
      >
        <Typography
          variant="caption"
          data-testid={`coverageCostId-${coverage.Object?.code}`}
          className={classes.priceCell}
        >
          {coverage.Object?.maxCost !== 0 ? (
            <>
              {formatMoney(coverage.Object?.maxCost || 0, false, typeOfMoney)}{' '}
              <br />
            </>
          ) : (
            'Gratis'
          )}
        </Typography>
        {coverage.Object?.maxCost !== 0 && (
          <Typography variant="caption" style={{ fontSize: '15px' }}>
            + IVA
          </Typography>
        )}
      </Box>
    );
  }

  function generateTitle(
    name: string,
    active: boolean,
    position: number,
  ): React.ReactNode {
    return (
      <TableCell style={{ border: 'none' }} className={classes.fixedCell}>
        <Box
          key={name}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          style={{ gap: '5px' }}
        >
          {active ? (
            <FontAwesomeIcon
              icon={faCircleCheck}
              color="#27AE60"
              style={{ fontSize: '24px' }}
            />
          ) : (
            <div style={{ width: '24px' }} />
          )}
          <Typography variant="h4">{name}</Typography>
          {generatePriceSubtitle(position)}
        </Box>
      </TableCell>
    );
  }

  return (
    <Grid container className={classes.container}>
      {/* Title bar (with close logic) */}
      <AppBar position="absolute" elevation={0} className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Grid container alignItems="center" justifyContent="center">
            <Typography className={classes.titleText} variant="body1">
              Comparación de beneficios
            </Typography>
          </Grid>
        </Toolbar>
      </AppBar>
      <div className={classes.toolbar} />

      <Table>
        <TableCell style={{ border: 'none' }} />
        {endorsements.map((coverage, index) =>
          generateTitle(
            formatCase(coverage.Object?.name),
            coverage.Value === selectedEndorsement,
            index,
          ),
        )}
      </Table>
      <Grid item xs={12} className={classes.noPaddingGrid}>
        <Table className={classes.table}>
          <TableBody>
            {/* Descriptions */}
            {tableSchema.map(
              (curSchema: Array<React.ReactNode>): React.ReactNode => {
                const [description, ...rest] = curSchema;
                return (
                  <TableRow className={classes.styledRow} key={uniqueId()}>
                    <TableCell className={classes.dynamicCell}>
                      <Typography
                        variant="caption"
                        className={classes.descriptionCell}
                      >
                        {description}
                      </Typography>
                    </TableCell>
                    {rest.map((data) => (
                      <TableCell className={classes.fixedCell} key={uniqueId()}>
                        <Typography
                          className={classes.informationCell}
                          variant="caption"
                        >
                          {data}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                );
              },
            )}
          </TableBody>
        </Table>
        <Box
          width="100%"
          display="flex"
          justifyContent="center"
          paddingTop="1rem"
        >
          <Typography
            variant="h4"
            style={{
              color: '#4A4B4E',
              fontWeight: 400,
              fontSize: 13,
              maxWidth: '350px',
            }}
          >
            Para mayor información sobre los beneficios o endosos, visita
            nuestro sitio web de{' '}
            <span style={{ color: colors.bright_turquoise, fontWeight: 700 }}>
              ASSA Seguro de Auto
            </span>
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default EndorsementComparison;
